/**
 * Karte „Modell-Zugang“ unter Einstellungen → Modelle. (12.10.2026, Bahn eigener-api-schluessel)
 *
 * Jedes Konto kann hier seinen EIGENEN Anthropic-Schlüssel hinterlegen; Vespera rechnet dann über die API mit ihm — Anthropic stellt
 * die Kosten direkt dem Nutzer in Rechnung. Konto 1 wählt zusätzlich, ob Vespera über sein Abo (wie bisher) oder über den Schlüssel antwortet.
 *
 * ⚠️ Der Schlüssel steht nur im Eingabefeld (Passwortfeld, ohne Autovervollständigung) und wird nach dem Senden geleert. Er kommt nie
 *    zurück: der Server nennt nur „endet auf ····abcd“. Die Seite legt ihn nirgends ab (kein Speicher im Browser, kein Protokoll).
 * 25.10.2026: dazu der Gemini-Schlüssel des Kontos (Bilder, Musik, Videos, Übersetzen) — eigener Teil, frontend/lib/gemini-schluessel-block.ts.
 * ⚠️ Ehrlich: Restguthaben zeigt Anthropic nicht an — die Karte sagt es und zeigt nur, was Vespera selbst verbraucht hat.
 */
import { baueStufenWahl } from "./stufen-wahl.js";
import { zeichen } from "./zeichen.js";
import { endetAufAnzeige, euroText, USD_IN_EUR } from "../../shared/api-zugang.js";
import { baueGeminiSchluessel, type GeminiStand } from "./gemini-schluessel-block.js";

export interface ZugangStand {
  ok: boolean;
  betreiber: boolean;
  zugang: "abo" | "api" | "kein-schluessel" | "gemini";
  modusWaehlbar: boolean;
  modus: "abo" | "api" | "gemini";
  schluessel: { gespeichert: boolean; endetAuf: string | null; geprueftAmMs: number | null; geprueftWort: string | null; geprueftSatz: string | null };
  limitCent: number;
  /** Geschätztes Restguthaben, fertig gerechnet vom Server (Euro-Cent) — null ohne Eintrag. */
  guthaben?: { startCent: number; restCent: number; seit: string } | null;
  modusGesperrt?: boolean;
  limitObergrenzeCent?: number | null;
  heuteKostenCent: number;
  heute: Nutzung;
  woche: Nutzung;
  monat: Nutzung;
  gemini?: GeminiStand;
}
export interface Nutzung {
  eingabe: number;
  ausgabe: number;
  cacheLesen: number;
  cacheSchreiben: number;
  kostenCent: number;
  zuege: number;
}

const KOPF = "x-vespera-aktion";
const KOPF_WERT = "api-zugang";

/** „vor 5 Minuten“ — grob, in einfachen Wörtern. */
export function vorZeit(ms: number, jetzt: number = Date.now()): string {
  const s = Math.max(0, Math.round((jetzt - ms) / 1000));
  if (s < 60) return "gerade eben";
  const m = Math.round(s / 60);
  if (m < 60) return m === 1 ? "vor einer Minute" : `vor ${m} Minuten`;
  const h = Math.round(m / 60);
  if (h < 24) return h === 1 ? "vor einer Stunde" : `vor ${h} Stunden`;
  const t = Math.round(h / 24);
  return t === 1 ? "vor einem Tag" : `vor ${t} Tagen`;
}

/** Die Statuszeile — ein Satz, keine Technik. */
export function statusText(s: ZugangStand, jetzt: number = Date.now()): { text: string; ton: "is-ok" | "is-warn" | "is-off" } {
  if (!s.schluessel.gespeichert) {
    return { text: s.betreiber ? "Kein Schlüssel gespeichert — Vespera nutzt dein Abo" : "Kein Schlüssel gespeichert", ton: "is-off" };
  }
  const ende = endetAufAnzeige(s.schluessel.endetAuf);
  const geprueft = s.schluessel.geprueftAmMs ? `, zuletzt geprüft ${vorZeit(s.schluessel.geprueftAmMs, jetzt)}` : "";
  const gut = s.schluessel.geprueftWort === "ok";
  return { text: `Schlüssel gespeichert, endet auf ${ende}${geprueft}${gut ? "" : " — bitte erneut prüfen"}`, ton: gut ? "is-ok" : "is-warn" };
}

/** Eine Nutzungszeile: „12.345 Token rein · 678 raus · ca. 0,45 € · 5 Antworten“. */
export function nutzungText(n: Nutzung): string {
  const zahl = (x: number): string => x.toLocaleString("de-AT");
  const rein = n.eingabe + n.cacheLesen + n.cacheSchreiben;
  if (n.zuege === 0) return "noch nichts verbraucht";
  const cache = n.cacheLesen + n.cacheSchreiben;
  const proZug = Math.round((rein + n.ausgabe) / n.zuege);
  return `${zahl(n.eingabe)} Token rein · ${zahl(n.ausgabe)} raus · ${zahl(cache)} Cache · ${euroText(n.kostenCent * USD_IN_EUR)} · ${n.zuege} ${n.zuege === 1 ? "Antwort" : "Antworten"} (Ø ${zahl(proZug)} Token je Antwort)`;
}

/**
 * Meldung „am Zugang wurde etwas gespeichert“ (Tageslimit, Guthaben, Modus, Schlüssel, Gemini). (27.10.2026)
 * Wer den Stand zeigt (Seitenleisten-Kachel, andere Karten) liest ihn danach sofort neu — ohne Neuladen.
 * `von` nennt den Absender, damit er sich nicht selbst neu zeichnet und seine Rückmeldung überschreibt.
 * Im Ereignis steht nie ein Schlüssel oder Betrag.
 */
export const ZUGANG_GEAENDERT = "vespera:zugang-geaendert";
export type ZugangQuelle = "modell-karte" | "guthaben-karte";

export async function hole(): Promise<ZugangStand | null> {
  try {
    const r = await fetch("/api/api-zugang", { credentials: "same-origin" });
    return r.ok ? ((await r.json()) as ZugangStand) : null;
  } catch {
    return null;
  }
}

export interface Antwort {
  ok: boolean;
  status: number;
  daten: (Partial<ZugangStand> & { satz?: string; error?: string }) | null;
}

export async function sende(pfad: string, koerper: unknown, von: ZugangQuelle = "modell-karte"): Promise<Antwort> {
  try {
    const r = await fetch(`/api/api-zugang/${pfad}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", [KOPF]: KOPF_WERT },
      body: JSON.stringify(koerper),
    });
    let daten: Antwort["daten"] = null;
    try {
      daten = (await r.json()) as Antwort["daten"];
    } catch {
      daten = null;
    }
    // Nur echte Speicherungen melden; „pruefen“ ändert nichts an Limit, Guthaben, Modus oder Schlüssel.
    if (r.ok && pfad !== "pruefen" && pfad !== "gemini-pruefen") document.dispatchEvent(new CustomEvent(ZUGANG_GEAENDERT, { detail: { von } }));
    return { ok: r.ok, status: r.status, daten };
  } catch {
    return { ok: false, status: 0, daten: null };
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = klasse;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** „5,00 €“ — das Limit ist ein fester Wert, keine Schätzung (anders als die Kosten). */
function eurosGenau(cent: number): string {
  return `${(Math.max(0, cent) / 100).toFixed(2).replace(".", ",")} €`;
}

/** „ca. 0,35 € von 5,00 €“ — dieselbe Zeile für diese Karte und die Verbrauchs-Kachel der Seitenleiste. */
export function heuteSatz(heuteKostenCent: number, limitCent: number): string {
  const kosten = euroText(heuteKostenCent * USD_IN_EUR);
  return verbrauchAnteil(heuteKostenCent, limitCent).stufe === "kein" ? `${kosten} — kein Tageslimit gesetzt` : `${kosten} von ${eurosGenau(limitCent)}`;
}

/** Nur der Betrag: „ca. 0,79 €“. */
export function kostenText(n: Nutzung): string {
  return euroText(n.kostenCent * USD_IN_EUR);
}

/** Eine kurze Zeile: „ca. 0,79 € · 12 Antworten“. */
export function kurzText(n: Nutzung): string {
  if (n.zuege === 0) return "noch nichts verbraucht";
  return `${euroText(n.kostenCent * USD_IN_EUR)} · ${n.zuege} ${n.zuege === 1 ? "Antwort" : "Antworten"}`;
}

/**
 * Wie viel vom Tageslimit verbraucht ist. Die Kosten kommen in US-Cent, das Limit in Euro-Cent (wie bisher: Umrechnung USD_IN_EUR).
 * Ohne Limit (0, fehlend, keine Zahl) gibt es keinen Balken. Der Satz sagt es in Worten, nie nur mit Farbe.
 */
export function verbrauchAnteil(kostenCentUsd: number, limitCent: number | null | undefined): { prozent: number; stufe: "ok" | "fast" | "voll" | "kein"; satz: string } {
  if (!Number.isFinite(limitCent) || !limitCent || limitCent <= 0) return { prozent: 0, stufe: "kein", satz: "" };
  const kosten = Number.isFinite(kostenCentUsd) ? Math.max(0, kostenCentUsd) * USD_IN_EUR : 0;
  const roh = (kosten / limitCent) * 100;
  const prozent = Math.min(100, roh);
  if (roh >= 100) return { prozent: 100, stufe: "voll", satz: "Limit erreicht — Vespera antwortet bis morgen nicht mehr." };
  if (roh >= 80) return { prozent, stufe: "fast", satz: "Limit fast erreicht." };
  return { prozent, stufe: "ok", satz: "" };
}

/** Der Grenzwert, ab dem das Restguthaben als „wird knapp“ gilt: unter 20 % vom Startbetrag. */
const GUTHABEN_KNAPP = 0.2;

/**
 * Das geschätzte Restguthaben für Karte und Seitenleiste. Die Zahlen kommen fertig vom Server (restCent, startCent);
 * hier wird nur formuliert und die Stufe gewählt. Unter 0: „aufgebraucht (Schätzung)“, ohne Minus-Betrag.
 * `prozent` ist der Rest vom Startbetrag (0–100) für den Balken.
 */
export function guthabenAnzeige(g: ZugangStand["guthaben"]): { satz: string; prozent: number; stufe: "ok" | "fast" | "voll" } | null {
  if (!g || !Number.isFinite(g.startCent) || !Number.isFinite(g.restCent) || g.startCent <= 0) return null;
  if (g.restCent <= 0) return { satz: "Guthaben aufgebraucht (Schätzung)", prozent: 0, stufe: "voll" };
  const anteil = g.restCent / g.startCent;
  return {
    satz: `Guthaben ${euroText(g.restCent)} (Schätzung)`,
    prozent: Math.min(100, anteil * 100),
    stufe: anteil < GUTHABEN_KNAPP ? "fast" : "ok",
  };
}

/** Zeile + Balken zum Restguthaben; leer, wenn nichts eingetragen ist. `zeilenKlasse` bestimmt, wo es steht (Karte oder Kachel). */
export function guthabenKnoten(g: ZugangStand["guthaben"], zeilenKlasse: string): HTMLElement[] {
  const a = guthabenAnzeige(g);
  if (!a || !g) return [];
  const zeile = el("p", zeilenKlasse, a.satz);
  zeile.dataset.stufe = a.stufe;
  const b = el("progress", "modell-zugang-balken modell-zugang-guthaben-balken");
  b.max = 100;
  b.value = a.prozent;
  b.dataset.stufe = a.stufe;
  b.setAttribute("aria-label", `Vom eingetragenen Guthaben sind noch etwa ${Math.round(a.prozent)} Prozent übrig (Schätzung)`);
  return [zeile, b];
}

/** Die Zeile direkt unter dem Feld: nur die LÄNGE des Eingefügten, nie der Schlüssel selbst. */
export function eingabeHinweis(laenge: number, anfangOk: boolean): { text: string; warnt: boolean } {
  if (laenge === 0) return { text: "", warnt: false };
  const eingefuegt = `Eingefügt — ${laenge} Zeichen`;
  return anfangOk ? { text: eingefuegt, warnt: false } : { text: `${eingefuegt}. Das sieht nicht wie ein Anthropic-Schlüssel aus (er beginnt mit sk-ant-).`, warnt: true };
}

type Ton = "is-ok" | "is-warn" | "is-off" | "is-fehler";

/** Eine Statuszeile setzen. Haken = geklappt, Kreuz = Fehler, sonst der Punkt in der Tonfarbe — der Text sagt es immer auch in Worten. */
function setzeStatusIn(status: HTMLElement, ton: Ton, text: string, ende = ""): void {
  while (status.firstChild) status.removeChild(status.firstChild);
  status.dataset.ton = ton;
  let marke: HTMLElement;
  if (ton === "is-ok" || ton === "is-fehler") {
    marke = el("span", `modell-zugang-marke ${ton}`);
    marke.innerHTML = zeichen(ton === "is-ok" ? "haken" : "schliessen", 16);
  } else marke = el("span", `connector-dot ${ton}`);
  const satz = el("span", "modell-zugang-satz");
  // „····abcd“ mit etwas Luft zwischen den Punkten: in dieser Schrift fließen vier Mittelpunkte sonst zu einem Strich zusammen.
  const stelle = ende ? text.indexOf(ende) : -1;
  if (stelle >= 0) satz.append(text.slice(0, stelle), el("span", "modell-zugang-ende", "····"), text.slice(stelle + 4));
  else satz.textContent = text;
  status.append(marke, satz);
}

const LINK_ERSTELLEN = "https://console.anthropic.com/settings/keys";
type Wahl = "abo" | "api" | "gemini";

export function createModellZugangKarte(): HTMLElement {
  const wrap = el("section", "settings-extension modell-zugang");
  const head = el("div", "settings-extension-head");
  head.appendChild(el("h3", "", "Modell-Zugang"));
  wrap.appendChild(head);

  // 1. Womit antwortet Vespera? (nur Konto 1 hat die Wahl; „Gemini“ erscheint nur, wenn der Betreiber es vorgibt)
  const modusBlock = el("div", "modell-zugang-modus");
  modusBlock.hidden = true;
  modusBlock.appendChild(el("p", "modell-zugang-frage", "Womit soll Vespera antworten?"));
  let lokalApi = false; // „Mein Schlüssel“ angetippt, aber noch keiner gespeichert: nur das Feld zeigen
  const wahlen: Array<{ wert: Wahl; text: string }> = [
    { wert: "abo", text: "Mein Abo" },
    { wert: "api", text: "Mein Schlüssel" },
  ];
  const geminiWahl = { wert: "gemini" as Wahl, text: "Gemini" };
  let mitGemini = false;
  const baueWahl = (): ReturnType<typeof baueStufenWahl<Wahl>> =>
    baueStufenWahl<Wahl>("Womit soll Vespera antworten?", mitGemini ? [...wahlen, geminiWahl] : wahlen, (w) => void waehleModus(w));
  let modusWahl = baueWahl();
  modusBlock.appendChild(modusWahl.el);
  wrap.appendChild(modusBlock);

  // Status: EINE Zeile. Dauerhafter Stand oder die letzte Rückmeldung (Farbe UND Zeichen/Text).
  const status = el("div", "connector-status modell-zugang-status");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.textContent = "Status wird geladen …";
  wrap.appendChild(status);

  // 2. Schlüssel einfügen (nur wenn „Mein Schlüssel“ gewählt oder noch keiner da ist)
  const schluesselBlock = el("div", "modell-zugang-schluessel");
  const form = el("form", "panel-karte-form modell-zugang-form");
  form.setAttribute("autocomplete", "off");
  const label = el("label", "panel-karte-label", "Schlüssel einfügen");
  label.htmlFor = "modell-zugang-feld";
  const feld = el("input", "panel-karte-feld modell-zugang-feld");
  feld.id = "modell-zugang-feld";
  feld.type = "password";
  feld.autocomplete = "off";
  feld.spellcheck = false;
  feld.setAttribute("autocapitalize", "off");
  feld.setAttribute("autocorrect", "off");
  feld.setAttribute("data-1p-ignore", "");
  feld.setAttribute("data-lpignore", "true");
  feld.name = "anthropic-zugang-" + Math.random().toString(36).slice(2, 8);
  feld.placeholder = "sk-ant-…";
  const eingabeZeile = el("p", "modell-zugang-eingabe");
  eingabeZeile.id = "modell-zugang-eingabe";
  eingabeZeile.setAttribute("role", "status");
  eingabeZeile.setAttribute("aria-live", "polite");
  feld.setAttribute("aria-describedby", eingabeZeile.id);
  const speichern = el("button", "connector-connect modell-zugang-speichern", "Speichern und prüfen");
  speichern.type = "submit";
  const erstellen = el("a", "modell-zugang-link", "Schlüssel erstellen");
  erstellen.href = LINK_ERSTELLEN;
  erstellen.target = "_blank";
  erstellen.rel = "noopener noreferrer";
  const knoepfe = el("div", "modell-zugang-reihe");
  knoepfe.append(speichern, erstellen);
  form.append(label, feld, eingabeZeile, knoepfe);
  schluesselBlock.append(form, el("p", "panel-karte-hinweis", "Der Schlüssel liegt verschlüsselt auf dem Server; Anthropic berechnet die Kosten direkt dir."));
  wrap.appendChild(schluesselBlock);

  // 3. Verbrauch und Tageslimit — sichtbar, sobald „Mein Schlüssel“ gilt; bei „Mein Abo“ nur eine ruhige Zeile.
  const aboZeile = el("p", "modell-zugang-abo", "Läuft über dein Abo, keine Extrakosten.");
  aboZeile.hidden = true;
  wrap.appendChild(aboZeile);
  const verbrauch = el("div", "modell-zugang-verbrauch");
  verbrauch.hidden = true;
  const heuteZeile = el("p", "modell-zugang-heute");
  const balken = el("progress", "modell-zugang-balken");
  balken.max = 100;
  balken.value = 0;
  const balkenSatz = el("p", "modell-zugang-balken-satz");
  balkenSatz.setAttribute("role", "status");
  const wocheZeile = el("p", "modell-zugang-klein");
  const monatZeile = el("p", "modell-zugang-klein");
  const limitZeile = el("div", "panel-karte-form modell-zugang-limit");
  const limitLabel = el("label", "panel-karte-label", "Tageslimit:");
  limitLabel.htmlFor = "modell-zugang-limit";
  const limitInnen = el("div", "modell-zugang-limitreihe");
  const limit = el("input", "panel-karte-feld modell-zugang-limitfeld");
  limit.id = "modell-zugang-limit";
  limit.type = "number";
  limit.min = "1";
  limit.max = "1000";
  limit.step = "0.5";
  limit.inputMode = "decimal";
  const limitKnopf = el("button", "connector-disconnect modell-zugang-knopf", "Speichern");
  limitKnopf.type = "button";
  limitKnopf.setAttribute("aria-label", "Tageslimit speichern");
  limitInnen.append(limit, el("span", "modell-zugang-euro", "€"), limitKnopf);
  limitZeile.append(limitLabel, limitInnen, el("p", "panel-karte-hinweis", "Maximal so viel pro Tag, dann antwortet Vespera bis morgen nicht mehr."));
  // Geschätztes Restguthaben: Zeile + Balken (nur mit Eintrag), darunter immer das Eintragen.
  const guthabenAnzeigeBox = el("div", "modell-zugang-guthaben-stand");
  // Eingetragen wird das Guthaben an EINEM Ort: Verbindungen -> „Konto und Guthaben“ (konto-guthaben-karte.ts). Hier nur der Verweis.
  const guthabenVerweis = el("div", "modell-zugang-guthaben-verweis");
  const guthabenKnopf = el("button", "connector-disconnect modell-zugang-knopf", "Zu Verbindungen");
  guthabenKnopf.type = "button";
  guthabenKnopf.setAttribute("aria-label", "Guthaben eintragen: zu Verbindungen, Karte Konto und Guthaben");
  guthabenKnopf.addEventListener("click", () => {
    document.dispatchEvent(new CustomEvent("vespera:go-settings", { detail: { sub: "verbindungen", fokus: "Konto und Guthaben" } }));
  });
  guthabenVerweis.append(el("p", "modell-zugang-klein", "Guthaben: unter Verbindungen"), guthabenKnopf);
  verbrauch.append(heuteZeile, balken, balkenSatz, wocheZeile, monatZeile, guthabenAnzeigeBox, guthabenVerweis);
  wrap.appendChild(verbrauch);
  wrap.appendChild(limitZeile); // das Limit lässt sich auch vorab setzen: ohne Schlüssel und im Abo

  // 4. Mehr: nur Preis-Hinweis, Erneut prüfen, Schlüssel löschen
  const mehr = el("details", "modell-zugang-mehr");
  mehr.appendChild(el("summary", "modell-zugang-mehr-kopf", "Mehr"));
  const mehrInhalt = el("div", "modell-zugang-mehr-inhalt");
  mehrInhalt.appendChild(
    el(
      "p",
      "panel-karte-hinweis",
      "Die Kosten sind eine Schätzung aus den Preisen von Anthropic. Dein Restguthaben zeigt Anthropic nicht an — schau dafür auf console.anthropic.com unter „Billing“.",
    ),
  );
  const aktionen = el("div", "connector-actions modell-zugang-aktionen");
  const pruefen = el("button", "connector-disconnect modell-zugang-knopf", "Erneut prüfen");
  pruefen.type = "button";
  const entfernen = el("button", "connector-disconnect modell-zugang-knopf", "Schlüssel löschen");
  entfernen.type = "button";
  aktionen.append(pruefen, entfernen);
  mehrInhalt.appendChild(aktionen);
  mehr.appendChild(mehrInhalt);
  wrap.appendChild(mehr);

  // 5. Der Gemini-Schlüssel (25.10.2026): eigener Teil, eigene Statuszeile — Bilder, Musik, Videos, Übersetzen.
  const gemini = baueGeminiSchluessel({ sende, el, vorZeit: (ms) => vorZeit(ms), setzeStatus: setzeStatusIn });
  wrap.appendChild(gemini.el);

  let letzter: ZugangStand | null = null;
  let letzterApi: boolean | null = null;

  const setzeStatus = (ton: Ton, text: string, ende = ""): void => setzeStatusIn(status, ton, text, ende);

  const zeige = (s: ZugangStand | null): void => {
    letzter = s;
    gemini.zeige(s?.gemini);
    if (!s) {
      setzeStatus("is-warn", "Der Stand ist gerade nicht abrufbar.");
      return;
    }
    if (s.schluessel.gespeichert) lokalApi = false;
    const { text, ton } = statusText(s);
    setzeStatus(ton, text, endetAufAnzeige(s.schluessel.endetAuf));
    modusBlock.hidden = !s.modusWaehlbar;
    const brauchtGemini = s.modus === "gemini" || s.zugang === "gemini";
    if (brauchtGemini !== mitGemini) {
      mitGemini = brauchtGemini;
      const neu = baueWahl();
      modusBlock.replaceChild(neu.el, modusWahl.el);
      modusWahl = neu;
    }
    const gewaehlt: Wahl = lokalApi ? "api" : s.modus;
    modusWahl.zeige(gewaehlt);
    for (const b of modusWahl.el.querySelectorAll("button")) b.disabled = s.modusGesperrt === true;
    schluesselBlock.hidden = s.modusWaehlbar && gewaehlt !== "api" && s.schluessel.gespeichert;
    pruefen.hidden = !s.schluessel.gespeichert;
    entfernen.hidden = !s.schluessel.gespeichert;
    limit.value = String(s.limitCent / 100);
    limit.max = s.limitObergrenzeCent ? String(s.limitObergrenzeCent / 100) : "1000";
    const mitApi = s.modus === "api" && s.schluessel.gespeichert;
    verbrauch.hidden = !mitApi;
    aboZeile.hidden = !(s.modusWaehlbar && s.modus === "abo");
    document.documentElement.dataset.zugang = mitApi ? "api" : "abo";
    if (mitApi !== letzterApi) {
      letzterApi = mitApi; // nur bei einem Wechsel melden: sonst klappt die Abo-Kachel alle 30 Sekunden wieder zu
      document.dispatchEvent(new CustomEvent("vespera:zugang", { detail: { api: mitApi } }));
    }
    const a = verbrauchAnteil(s.heuteKostenCent, s.limitCent);
    heuteZeile.textContent = `Heute: ${heuteSatz(s.heuteKostenCent, s.limitCent)}`;
    balken.hidden = a.stufe === "kein";
    balken.value = a.prozent;
    balken.setAttribute("aria-label", `Heute verbraucht: ${Math.round(a.prozent)} Prozent des Tageslimits`);
    balken.dataset.stufe = a.stufe;
    balkenSatz.textContent = a.satz;
    balkenSatz.dataset.stufe = a.stufe;
    balkenSatz.hidden = a.satz === "";
    wocheZeile.textContent = `Diese Woche: ${kurzText(s.woche)}`;
    monatZeile.textContent = `Diesen Monat: ${kurzText(s.monat)}`;
    guthabenAnzeigeBox.replaceChildren(...guthabenKnoten(s.guthaben, "modell-zugang-guthaben-zeile"));
  };

  const SATZ_NETZ = "Der Server ist gerade nicht erreichbar. Bitte versuch es gleich noch einmal.";
  // Laufnummer: eine ältere, später eintreffende Antwort überschreibt nie einen neueren Stand.
  let lauf = 0;
  /** Antwort auf eine eigene Speicherung: gilt als neuester Stand, eine ältere Abfrage danach wird verworfen. */
  const nimm = (s: ZugangStand): void => {
    lauf++;
    zeige(s);
  };
  const frisch = async (): Promise<void> => {
    const nr = ++lauf;
    const s = await hole();
    if (nr === lauf) zeige(s);
  };
  const fehler = (text: string): void => setzeStatus("is-fehler", text);
  const gut = (text: string): void => setzeStatus("is-ok", text);

  const waehleModus = async (wert: Wahl): Promise<void> => {
    if (!letzter) return;
    if (wert === "api" && !letzter.schluessel.gespeichert) {
      // Ohne Schlüssel gibt es nichts zu schalten (der Server sagt auch 409): nur das Feld zeigen.
      lokalApi = true;
      zeige(letzter);
      feld.focus();
      return;
    }
    lokalApi = false;
    if (wert === letzter.modus) {
      zeige(letzter);
      return;
    }
    const r = await sende("modus", { modus: wert });
    if (r.ok && r.daten) {
      nimm(r.daten as ZugangStand);
      gut(wert === "api" ? "Vespera antwortet jetzt mit deinem Schlüssel." : wert === "gemini" ? "Vespera antwortet jetzt mit Gemini." : "Vespera antwortet wieder mit deinem Abo.");
    } else {
      fehler(r.status === 409 ? "Dafür brauchst du zuerst einen gespeicherten Schlüssel." : SATZ_NETZ);
      void frisch();
    }
  };

  // Sichtbares Feedback beim Einfügen: nur die Länge, nie der Schlüssel.
  const zeigeEingabe = (): void => {
    const wert = feld.value.trim();
    const h = eingabeHinweis(wert.length, wert.startsWith("sk-ant-"));
    eingabeZeile.textContent = h.text;
    eingabeZeile.classList.toggle("is-warnt", h.warnt);
  };
  feld.addEventListener("input", zeigeEingabe);
  feld.addEventListener("change", zeigeEingabe);
  feld.addEventListener("paste", () => window.setTimeout(zeigeEingabe, 0));

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const wert = feld.value.trim();
    if (!wert) {
      fehler("Bitte füge zuerst deinen Schlüssel ein.");
      return;
    }
    // Sofort leeren: der Schlüssel bleibt nicht länger im Feld stehen als nötig.
    feld.value = "";
    zeigeEingabe();
    speichern.disabled = true;
    setzeStatus("is-warn", "Prüfe …");
    const wollteApi = lokalApi;
    void sende("schluessel", { schluessel: wert }).then(async (r) => {
      speichern.disabled = false;
      if (r.ok && r.daten) {
        const neu = r.daten as ZugangStand;
        nimm(neu);
        if (neu.schluessel.geprueftWort === "ok") {
          const ende = endetAufAnzeige(neu.schluessel.endetAuf);
          setzeStatus("is-ok", `Gespeichert und geprüft — endet auf ${ende}`, ende);
          // Wer „Mein Schlüssel“ gewählt hatte, bekommt ihn jetzt auch wirklich.
          if (wollteApi && neu.modusWaehlbar && neu.modus !== "api") await waehleModus("api");
        } else setzeStatus("is-warn", r.daten.satz ?? "Gespeichert, aber die Prüfung war nicht sauber. Bitte erneut prüfen.");
      } else {
        fehler(r.daten?.satz ?? (r.status === 429 ? "Bitte warte eine Minute, dann geht es weiter." : r.status === 0 ? SATZ_NETZ : "Das hat nicht geklappt. Der Schlüssel wurde nicht gespeichert."));
      }
    });
  });

  pruefen.addEventListener("click", () => {
    pruefen.disabled = true;
    setzeStatus("is-warn", "Prüfe …");
    void sende("pruefen", {}).then((r) => {
      pruefen.disabled = false;
      if (r.daten && "schluessel" in r.daten) nimm(r.daten as ZugangStand);
      const satz = r.daten?.satz ?? (r.status === 429 ? "Bitte warte eine Minute, dann geht es weiter." : SATZ_NETZ);
      if (r.ok && r.daten?.schluessel?.geprueftWort === "ok") gut(satz);
      else fehler(satz);
    });
  });

  entfernen.addEventListener("click", () => {
    void (async () => {
      const { confirmSheet } = await import("./sheet-ui.js");
      const ja = await confirmSheet({
        title: "Schlüssel löschen?",
        confirmLabel: "Löschen",
        danger: true,
      });
      if (!ja) return;
      const r = await sende("entfernen", {});
      if (r.ok && r.daten) {
        nimm(r.daten as ZugangStand);
        gut("Der Schlüssel ist gelöscht.");
      } else fehler(SATZ_NETZ);
    })();
  });

  limitKnopf.addEventListener("click", () => {
    const euro = Number(limit.value.replace(",", "."));
    void sende("limit", { euro }).then((r) => {
      if (r.ok && r.daten) {
        nimm(r.daten as ZugangStand);
        gut("Das Limit ist gespeichert.");
      } else fehler(r.status === 400 ? "Bitte nimm eine Zahl zwischen 1 und 1000 Euro." : SATZ_NETZ);
    });
  });

  void frisch();
  // Hat eine andere Stelle (z. B. die Guthaben-Karte unter Verbindungen) etwas gespeichert: Stand neu lesen.
  const beiAenderung = (ev: Event): void => {
    if (!wrap.isConnected) document.removeEventListener(ZUGANG_GEAENDERT, beiAenderung);
    else if ((ev as CustomEvent<{ von?: string }>).detail?.von !== "modell-karte") void frisch();
  };
  document.addEventListener(ZUGANG_GEAENDERT, beiAenderung);
  // Die Nutzung wandert während des Gesprächs mit: alle 30 s neu lesen, solange die Karte sichtbar im Dokument steht.
  const takt = window.setInterval(() => {
    if (!wrap.isConnected) window.clearInterval(takt);
    else if (!document.hidden && letzter) void frisch();
  }, 30_000);
  return wrap;
}
